import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { PlanFeature } from '@models/asset-details.model';
import { DeviceAsset, LineAsset, UserAsset } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  carrierLogoUrl,
  fieldValueIsPresent,
  generateAssetImageUrl
} from '@models/functions.model';
import { AssetDisplayInfo, AssetFieldDefinition, ImageMetaData } from '@models/my-assets.model';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { TranslationService } from '@services/translation/translation.service';
import { EligibilityBadgeComponent } from '@shared-components/badge/eligibility-badge/eligibility-badge.component';
import { StatusBadgeComponent } from '@shared-components/badge/status-badge/status-badge.component';
import { Subscription } from 'rxjs';

const FIELDS_DEFINITION: Map<string, AssetFieldDefinition> = new Map<string, AssetFieldDefinition>([
  ['imei', { label: 'IMEI' }],
  ['imei2', { label: 'IMEI2' }],
  ['imei_associated', { label: 'IMEI Associated for this Line' }],
  ['eid', { label: 'EID' }],
  ['serial_number', { label: 'Serial Number' }],
  ['sim_compatibility', { label: 'SIM/ICCID Compatibility' }],
  ['plan', { label: 'Plan' }],
  ['sim_number', { label: 'SIM Number' }],
  ['sim_number_label', { label: 'ICCID/SIM/eSIM Number' }],
  ['plan_features', { label: 'Plan features' }],
  ['add_on_features', { label: 'Add-On Features' }],
  ['contract_end_date', { label: 'Contract End Date' }],
  ['contract_eligibility_date', { label: 'Contract Eligibility Date' }],
  ['upgrade_eligible_date', { label: 'Upgrade Eligibility Date' }],
  ['carrier_account', { label: 'Carrier Account' }]
]);

@Component({
  selector: 'ts-asset-selection-item',
  templateUrl: './asset-selection-item.component.html',
  imports: [
    CommonModule,
    NgbAccordionModule,
    StatusBadgeComponent,
    EligibilityBadgeComponent,
    TranslationPipe
  ]
})
export class AssetSelectionItemComponent implements OnInit, OnDestroy {
  @HostBinding('class') class: string = 'd-contents';
  @Input() asset: UserAsset;
  @Input() additional_asset: DeviceAsset | LineAsset;
  @Input() additional_asset_type: string;
  @Input() asset_fields: string[] = [
    'imei',
    'imei2',
    'imei_associated',
    'serial_number',
    'eid',
    'sim_compatibility',
    'plan',
    'sim_number',
    'add_on_features',
    'plan_features',
    'contract_end_date',
    'upgrade_eligible_date',
    'carrier'
  ];
  @Input() is_offcanvas: boolean = false;
  asset_display_infos: AssetDisplayInfo[] = [];
  order_type_id: number = -1;
  multi_sim_types: string[] = MULTI_SIM_TYPES;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_state_svc: ActivityStateService,
    private translation_svc: TranslationService
  ) {
    this.order_type_id = this.activity_state_svc.orderTypeId()!;
  }

  ngOnInit(): void {
    this.asset_display_infos = this.populateInfos();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getImageMetaData(image_url: string | null | undefined): ImageMetaData {
    if (this.additional_asset) {
      if (this.additional_asset_type === 'line') {
        const line: LineAsset = this.additional_asset as LineAsset;
        return { src: carrierLogoUrl(line.carrier_image_url), alt: line.carrier_name ?? '' };
      }
      const device: DeviceAsset = this.additional_asset as DeviceAsset;
      return {
        src: device.image_url,
        alt: `${device.manufacturer ?? ''} ${device.model ?? ''}`.trim() || 'Device image'
      };
    }
    if (
      this.is_offcanvas &&
      [this.multi_sim_types[1], this.multi_sim_types[2], this.multi_sim_types[6]].includes(
        this.asset.multi_sim_type
      )
    ) {
      return {
        src: carrierLogoUrl(this.asset.line?.carrier_image_url),
        alt: this.asset.line?.carrier_name ?? ''
      };
    }
    return {
      src: image_url?.replace(/(thumb|original)/, 'medium'),
      alt:
        `${this.asset.device?.manufacturer ?? ''} ${this.asset.device?.model ?? ''}`.trim() ||
        'Device image'
    };
  }

  fieldExists(field: string | undefined): string | undefined {
    return fieldValueIsPresent(field);
  }

  isValueArray(value: string | string[]): boolean {
    return Array.isArray(value);
  }

  imageLoadError(event: any, asset: UserAsset): void {
    const is_only_line: string =
      !asset.device?.type && asset.line ? 'line' : asset.device?.type || 'other';
    event.target.src = generateAssetImageUrl(is_only_line);
  }

  additionalAssetTitle(): string {
    let title: string = '';

    if (!this.additional_asset) {
      switch (this.asset.multi_sim_type) {
        case this.multi_sim_types[0]:
        case this.multi_sim_types[2]:
        case this.multi_sim_types[4]:
          title += this.asset.line.international_number;
          return title;
        case this.multi_sim_types[1]:
        case this.multi_sim_types[3]:
        case this.multi_sim_types[5]:
        case this.multi_sim_types[6]:
          if (this.asset.device?.manufacturer) title += this.asset.device?.manufacturer + ' ';
          if (this.asset.device?.model) title += this.asset.device?.model + ' ';
          if (this.asset.device?.size) title += this.asset.device?.size + ' ';
          if (this.asset.device?.color) title += ' - ' + this.asset.device?.color;
          return title;
      }
    }

    switch (this.additional_asset_type) {
      case 'device':
        if ((this.additional_asset as DeviceAsset)?.manufacturer)
          title += (this.additional_asset as DeviceAsset)?.manufacturer + ' ';
        if ((this.additional_asset as DeviceAsset)?.model)
          title += (this.additional_asset as DeviceAsset)?.model + ' ';
        if ((this.additional_asset as DeviceAsset)?.size)
          title += (this.additional_asset as DeviceAsset)?.size + ' ';
        if ((this.additional_asset as DeviceAsset)?.color)
          title += ' - ' + (this.additional_asset as DeviceAsset)?.color;
        break;
      case 'line':
        title += (this.additional_asset as LineAsset).international_number;
        break;
      default:
        break;
    }

    return title;
  }

  additionalAssetAsLine(): LineAsset {
    if (!this.additional_asset) return this.asset.line;
    else return this.additional_asset as LineAsset;
  }

  additionalAssetAsDevice(): DeviceAsset {
    if (!this.additional_asset) return this.asset.device;
    else return this.additional_asset as DeviceAsset;
  }

  additionalAssetIsPrimary(): boolean {
    switch (this.asset.multi_sim_type) {
      case this.multi_sim_types[0]:
        return (this.additional_asset as LineAsset)?.asset_primary_line ||
          (this.asset?.line?.asset_primary_line && !this.additional_asset)
          ? true
          : false;
      case this.multi_sim_types[1]:
      case this.multi_sim_types[6]:
        return (this.additional_asset as DeviceAsset)?.asset_primary_device ||
          (this.asset?.device?.asset_primary_device && !this.additional_asset)
          ? true
          : false;
      default:
        return false;
    }
  }

  showLineBadges(multi_sim_type: string): boolean {
    return (
      this.additional_asset_type === this.multi_sim_types[4] ||
      [this.multi_sim_types[0], this.multi_sim_types[2]].includes(multi_sim_type)
    );
  }

  private populateInfos(): AssetDisplayInfo[] {
    return this.asset_fields.reduce((acc, field) => {
      if (!FIELDS_DEFINITION.has(field)) return acc;

      const definition = FIELDS_DEFINITION.get(field)!;
      const value = this.getFieldValue(field, this.asset);

      let new_field: AssetDisplayInfo = {
        field,
        label: this.translation_svc.translate(field) ?? definition.label,
        value: this.normalizedValue(value)
      };

      acc.push(new_field);

      return acc;
    }, [] as AssetDisplayInfo[]);
  }

  private getFieldValue(field: string, asset: UserAsset): string | string[] | undefined {
    let value: string | string[] | undefined = undefined;

    switch (field) {
      case 'imei':
        if (this.additional_asset)
          value =
            (this.additional_asset as DeviceAsset)?.imei ??
            (this.additional_asset as LineAsset)?.associated_device_imei;
        else value = asset.device?.imei;
        break;
      case 'imei2':
        if (this.additional_asset) value = (this.additional_asset as DeviceAsset)?.imei2;
        else value = asset.device?.imei2;
        break;
      case 'imei_associated':
        if (this.additional_asset)
          value =
            (this.additional_asset as DeviceAsset)?.associated_device_imei ??
            (this.additional_asset as LineAsset)?.associated_device_imei;
        else value = asset.device?.associated_device_imei ?? asset.line?.associated_device_imei;
        break;
      case 'eid':
        if (this.additional_asset) value = (this.additional_asset as DeviceAsset)?.eid;
        else value = asset.device?.eid;
        break;
      case 'serial_number':
        if (this.additional_asset) value = (this.additional_asset as DeviceAsset)?.serial_number;
        else value = asset.device?.serial_number;
        break;
      case 'sim_compatibility':
        if (this.additional_asset)
          value = (this.additional_asset as DeviceAsset)?.tech_specs?.sim_compatibility;
        else value = asset.device?.tech_specs?.sim_compatibility;
        break;
      case 'plan':
        if (this.additional_asset) value = (this.additional_asset as LineAsset)?.plan?.name;
        else value = asset.line?.plan?.name;
        break;
      case 'sim_number':
      case 'sim_number_label':
        if (this.additional_asset)
          value =
            (this.additional_asset as DeviceAsset)?.sim_number ??
            (this.additional_asset as LineAsset)?.sim_number;
        else value = asset.line?.sim_number;
        break;
      case 'add_on_features':
        if (this.additional_asset)
          value = this.formatFeatures((this.additional_asset as LineAsset)?.plan?.features);
        else value = this.formatFeatures(asset.line?.plan?.features);
        break;
      case 'contract_end_date':
        if (this.additional_asset) value = (this.additional_asset as LineAsset)?.contract_end_date;
        else value = asset.line?.contract_end_date;
        break;
      case 'upgrade_eligible_date':
        if (this.additional_asset)
          value = (this.additional_asset as LineAsset)?.upgrade_eligible_date;
        else value = asset.line?.upgrade_eligible_date;
        break;
      case 'plan_features':
        if (this.additional_asset)
          value = this.formatFeatures(
            (this.additional_asset as LineAsset)?.plan?.embedded_features
          );
        else value = this.formatFeatures(asset.line?.plan?.embedded_features);
        break;
      case 'carrier':
        if (this.additional_asset) value = (this.additional_asset as LineAsset)?.carrier_name;
        else value = asset.line?.carrier_name;
        break;
      case 'carrier_account':
        if (this.additional_asset)
          value = (this.additional_asset as LineAsset)?.carrier_account?.display_name;
        else value = asset.line?.carrier_account?.display_name;
        break;
    }

    return value;
  }

  private normalizedValue(value: string | string[] | undefined | null): string | string[] {
    if (!value || (Array.isArray(value) && !value.length)) return 'N/A';
    return value;
  }

  private formatFeatures(items: PlanFeature[] | undefined): string[] {
    if (!items) return [];

    return (items ?? []).reduce((acc, feature) => {
      if (feature.name) acc.push(feature.name);
      return acc;
    }, [] as string[]);
  }
}
